import { useCallback, useMemo } from "react";
import { type TableColumnType } from "@ir/ui";
import { GetMasterDataRetailerMatchRulesDTO } from "@src/domain/dto/masterData";
import type { MasterDataRetailerMatchRule as MasterDataRetailerMatchRuleEntity } from "@src/domain/entities/masterData";
import { GetMasterDataRetailerMatchRulesUseCase } from "@src/application/useCases/masterData";
import { MasterDataRetailerMatchRuleRepository } from "@src/infrastructure/repositories/masterData";
import { useMasterDataRetailers } from "@src/presentation/providers/MasterDataRetailersDataProvider";
import { masterDataRetailerMatchRulesCacheKey } from "@src/presentation/providers/masterData/masterDataListCache";
import {
  MasterDataRetailerChildListModal,
  type MasterDataRetailerChildModalHostProps,
} from "./MasterDataRetailerChildListModal";
import { MasterDataRetailerMatchRuleAddEditForm } from "./MasterDataRetailerMatchRuleAddEditForm";
import {
  MASTER_DATA_COLUMN_VERTICAL_ALIGN,
  renderMasterDataTextCell,
} from "./masterDataTable.shared";
import {
  buildMatchRuleActionsColumn,
  indexMatchRulesById,
  mapMatchRulesToRows,
  type MasterDataRetailerMatchRuleRow,
} from "./masterDataRetailerMatchRuleTable.shared";
import { useMasterDataRetailerChildList } from "./useMasterDataRetailerChildList";
import { useMasterDataRetailerMatchRuleForm } from "./useMasterDataRetailerMatchRuleForm";
import { useMasterDataRetailerMatchSourceColumns } from "./useMasterDataRetailerMatchSourceColumns";

function buildColumns(
  onEdit: (entity: MasterDataRetailerMatchRuleEntity) => void,
  rulesById: Map<number, MasterDataRetailerMatchRuleEntity>,
): TableColumnType<MasterDataRetailerMatchRuleRow>[] {
  return [
    {
      title: "Match Pattern",
      dataIndex: "matchPattern",
      key: "matchPattern",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 260,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "Match Priority",
      dataIndex: "matchPriority",
      key: "matchPriority",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 140,
    },
    {
      title: "Source Column",
      dataIndex: "sourceColumn",
      key: "sourceColumn",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 160,
      cellRender: renderMasterDataTextCell,
    },
    buildMatchRuleActionsColumn(onEdit, rulesById),
  ];
}

export const MasterDataRetailerMatchRulesModal = ({
  isOpen,
  retailerId,
  retailerName,
  onClose,
}: MasterDataRetailerChildModalHostProps) => {
  const { brandKey, refetch: refetchRetailers } = useMasterDataRetailers();
  const {
    page,
    setPage,
    pageSize,
    items,
    itemTotal,
    loading,
    error,
    handleSaved,
    resetOnClose,
  } = useMasterDataRetailerChildList<MasterDataRetailerMatchRuleEntity>({
    isOpen,
    retailerId,
    brandKey,
    loadErrorFallback: "Failed to load match rules",
    buildCacheKey: masterDataRetailerMatchRulesCacheKey,
    invalidateScopes: ["retailer-match-rules-modal", "retailers"],
    fetchPage: (id, pageNumber, limit) =>
      new GetMasterDataRetailerMatchRulesUseCase(
        new MasterDataRetailerMatchRuleRepository(),
      ).execute(
        new GetMasterDataRetailerMatchRulesDTO({
          retailerId: id,
          search: null,
          page_number: pageNumber,
          page_size: limit,
        }),
      ),
    refetchParent: refetchRetailers,
  });

  const { sourceColumns, isReady: sourceColumnsReady } =
    useMasterDataRetailerMatchSourceColumns(isOpen, brandKey);

  const {
    form,
    formMode,
    isSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    initialValues,
    lockedRetailerName,
    openCreate,
    openEdit,
    closeForm,
    handleSubmit,
  } = useMasterDataRetailerMatchRuleForm({
    onSaved: handleSaved,
    lockedRetailerId: retailerId,
    lockedRetailerName: retailerName,
    sourceColumns,
  });

  const rulesById = useMemo(() => indexMatchRulesById(items), [items]);
  const columns = useMemo(
    () => buildColumns(openEdit, rulesById),
    [openEdit, rulesById],
  );
  const dataSource = useMemo(() => mapMatchRulesToRows(items), [items]);

  const handleClose = useCallback(() => {
    closeForm();
    resetOnClose();
    onClose();
  }, [closeForm, onClose, resetOnClose]);

  const formStrip =
    formMode != null ? (
      <MasterDataRetailerMatchRuleAddEditForm
        form={form}
        mode={formMode}
        initialValues={initialValues}
        lockedRetailerName={lockedRetailerName}
        isSaving={isSaving}
        isFormSubmittable={isFormSubmittable}
        onSubmit={handleSubmit}
        onCancel={() => closeForm()}
        onFormValidityChange={setIsFormSubmittable}
        sourceColumns={sourceColumns}
      />
    ) : null;

  return (
    <MasterDataRetailerChildListModal
      isOpen={isOpen}
      title={retailerName ? `Match Rules — ${retailerName}` : "Match Rules"}
      addButtonLabel="Add Match Rule"
      emptyTitle="No match rules for this retailer."
      tableAriaLabel="Retailer match rules"
      paginationAriaLabel="Retailer match rules pagination"
      loading={loading}
      error={error}
      isFormOpen={formMode != null}
      formStrip={formStrip}
      columns={columns}
      dataSource={dataSource}
      page={page}
      pageSize={pageSize}
      itemTotal={itemTotal}
      onPageChange={setPage}
      onAdd={openCreate}
      onClose={handleClose}
      disableAdd={!sourceColumnsReady || sourceColumns.length === 0}
    />
  );
};
